import type { RenderImage } from '../../utils/image/renderImage';
import type { AntiBotMeta } from '../../constants/antiBot';
import type { UnitGroupConfig, UnitGroupingMode } from '@autodromo/units';
import type { useGlobalQueryParams } from '../../hooks/queryParams/useGlobalQueryParams';

/* =====================================================================
   Redirecionamento (dinâmica padrão do sistema). Tipo local do blocks —
   espelha o shape serializado pelo UI (apps/ui/src/common/types/redirect.ts),
   para não puxar nada de Next/UI para a seção federada.
   ===================================================================== */
export enum MenuRedirectType {
  PAGE = 'page',
  EXTERNAL_URL = 'external_url',
  SCROLL_TO_BLOCK = 'scroll_to_block',
  OPEN_MODAL = 'open_modal',
}

export interface MenuRedirectConfig {
  type: MenuRedirectType;
  pageSlug?: string;
  url?: string;
  openInNewTab?: boolean;
  scrollPageSlug?: string;
  blockId?: string;
}

/* =====================================================================
   Tipos do domínio (resolvidos do CMS em runtime; fallback nos mocks)
   ===================================================================== */
export interface VehicleVersion {
  id: string;
  name: string;
  price: number;
  specs: string[];
  image?: string;
  /** 360 da versão (campo galeria, ex.: "Galeria 360" / rotation_gallery). */
  frames360?: string[];
}

export interface VehicleModel {
  id: string;
  name: string;
  image: string;
  frames360?: string[];
  versions: VehicleVersion[];
}

export interface SimUnit {
  id: string;
  name: string;
  whatsapp: string;
  phone?: string;
  address?: string;
  openingHours?: string;
  mapLink?: string;
}

/** Estrutura dinâmica (mesma resolvida pelo builder e pelo renderer) que a
    seção converte em `VehicleModel[]` + `SimUnit[]` via `mapEntries`. */
export interface SimuladorDynamicStructure {
  section?: unknown;
  item?: Record<string, unknown> | null;
  channel?:
    | (Record<string, unknown> & {
        units?: unknown[];
        unitGroupingMode?: UnitGroupingMode | null;
        unitGroups?: UnitGroupConfig[] | null;
      })
    | null;
}

export interface BackgroundImage {
  desktop: string;
  mobile: string;
  alt: string;
}

/* =====================================================================
   Configuração de financiamento e FIPE. A simulação genérica é uma
   divisão simples (sem juros/CET — contexto de banco fica no bloco DDM);
   os prazos são configuráveis no sidebar (campo `installmentTerms`).
   ===================================================================== */
/** % mínimo do valor do veículo que precisa ficar financiado (clamp da entrada). */
export const MIN_FINANCED_PERCENT = 0.05;

/** Prazos padrão (meses) — o Champion adiciona/remove no sidebar. */
export const DEFAULT_INSTALLMENT_TERMS: number[] = [12, 18, 24, 30, 36];

/* =====================================================================
   Textos internos (não editáveis no sidebar — só conteúdo do wizard)
   ===================================================================== */
export const BANK_NAME = 'Banco parceiro';

export const LGPD_LABEL =
  'Li e aceito a Política de privacidade e concordo em receber comunicações da concessionária.';

/** Texto rico padrão da Política de privacidade (editável no sidebar). */
export const DEFAULT_PRIVACY_TEXT =
  '<p>Seus dados pessoais poderão ser utilizados pela concessionária e pela instituição financeira parceira para fins de envio da sua proposta e comunicações de marketing de produtos e serviços relacionados.</p>' +
  '<p>Ao preencher os dados acima, você está ciente e concorda que a concessionária compartilhará os seus dados pessoais com as instituições financeiras parceiras, para fins de envio de propostas e comunicações de marketing de produtos financeiros e securitários.</p>' +
  '<p>Para mais detalhes sobre como tratamos seus dados pessoais, acesse a Política de privacidade.</p>';

export const LEGAL_TEXT =
  'Ao enviar, você autoriza o contato da concessionária e da instituição financeira parceira para análise de crédito. Os valores e taxas apresentados são simulações e não constituem oferta de crédito; estão sujeitos à análise e aprovação. O Custo Efetivo Total (CET) será informado previamente à contratação. Conheça nossa Política de Privacidade.';

/** Texto rico padrão do aviso legal do resumo (editável no sidebar). */
export const DEFAULT_SUMMARY_LEGAL_TEXT = `<p>${LEGAL_TEXT}</p>`;

export const SUCCESS_WITHOUT_SUBMIT_TITLE = 'Simulação realizada com sucesso';

export const DISCLAIMER_TEXT =
  'A concessionária é correspondente no país e atua no encaminhamento de propostas de financiamento de veículos para a instituição financeira parceira. Os valores apresentados são simulações e não constituem oferta de crédito. A contratação está sujeita à análise e aprovação de crédito. Consulte sempre as condições e o Custo Efetivo Total (CET) previamente à contratação.';


/* =====================================================================
   Props do bloco
   ===================================================================== */
export interface SimuladorCtaButton {
  isActive: boolean;
  label: string;
  redirect: MenuRedirectConfig;
}

export interface SimuladorFinanciamentoProps {
  htmlId?: string;
  preload?: boolean;

  /* Conteúdo editável */
  title?: string;
  titleVisible?: boolean;
  titleTag?: string;
  description?: string;
  descriptionVisible?: boolean;
  privacyPolicyText?: string;
  /** @deprecated legado — mantido como fallback de leitura; use `privacyPolicyRedirect`. */
  privacyPolicyUrl?: string;
  privacyPolicyRedirect?: MenuRedirectConfig;

  /* Etapa 2 — Seus dados (campo exibido = campo obrigatório) */
  showCpf?: boolean;
  showBirthdate?: boolean;

  /* Etapa 4 — Simulação */
  installmentTerms?: number[];

  /* Etapa 5 — Resumo */
  summaryLegalText?: string;
  submitEnabled?: boolean;
  ctaButton?: SimuladorCtaButton;

  /* Design */
  activePalette?: string;
  backgroundType?: 'color' | 'image';
  bgColor?: string;
  backgroundImage?: BackgroundImage;
  titleColor?: string;
  bodyColor?: string;
  accentColor?: string;
  ctaBgColor?: string;
  ctaTextColor?: string;

  /* Layout */
  padding?: {
    typeMeasure?: string;
    desktop?: { paddingTop?: number; paddingBottom?: number };
    mobile?: { paddingTop?: number; paddingBottom?: number };
  };

  /* Dados: a `dynamicStructure` é resolvida internamente (mapEntries); ou o
     wrapper injeta `vehicles`/`units` já prontos (ex.: mock/story). */
  dynamicStructure?: SimuladorDynamicStructure;
  vehicles?: VehicleModel[];
  units?: SimUnit[];
  loading?: boolean;
  /** Injetada pelo wrapper: `<img>` (builder) ou `next/image` (renderer). */
  renderImage?: RenderImage;
  /** Injetados pelo renderer (Node.tsx) para registrar o lead no crm-api. */
  accountId?: string;
  siteId?: string;
  teamId?: string;
  /** Config antibot do site (Node.tsx via resolveAntiBotMeta) — sem token o
      crm-api bloqueia a conversão (403) onde o antibot está ativo. */
  antiBot?: AntiBotMeta | null;
  /** Injetada pelo wrapper SSR: no builder fica ausente (noop) e o bloco não lê a URL. */
  useGlobalQueryParams?: typeof useGlobalQueryParams;
  /** Builder: quando a coleção conectada não tem modelos de veículo válidos,
      mostra um aviso (EmptyState) em vez de sumir. No publicado fica false → o
      bloco some (não mostra aviso pro visitante). */
  showEmptyWarning?: boolean;

  hideOnMobile?: boolean;
  hideOnDesktop?: boolean;
}
